import { EPersonType, EProposalFileStatus, EProposalFileType, EProposalOrigin, ProposalCustomerPendenciesFields, ProposalLegalRepresentativePendenciesFields, ProposalModel } from '~/graphql/types'

/**
 * Verifica se a proposta é de origem partner (partner, partnerApi ou partnerLink)
 * @param proposal - A proposta a ser verificada
 * @returns true se for de origem partner, false caso contrário
 */
export const isPartnerOriginProposal = (proposal: ProposalModel | null): boolean => {
  if (!proposal) return false
  
  const partnerOrigins = [
    EProposalOrigin.partner,
    EProposalOrigin.partnerApi,
    EProposalOrigin.partnerLink
  ]
  
  return partnerOrigins.includes(proposal.origin)
}

export const isPendencyField = (fieldName: keyof ProposalCustomerPendenciesFields, pendencies?: ProposalCustomerPendenciesFields | null) => {
  if (!pendencies) {
    return false
  }

  const pendencyFields = Object.entries(pendencies).filter(pendency => pendency[1].rejectedAt && !pendency[1].acceptedAt).map(pendency => pendency[0])

  return pendencyFields.includes(fieldName)
}

export const isLegalRepresentativePendencyField = (fieldName: keyof ProposalLegalRepresentativePendenciesFields, pendencies?: ProposalLegalRepresentativePendenciesFields | null) => {
  if (!pendencies) {
    return false
  }

  const pendencyFields = Object.entries(pendencies).filter(pendency => pendency[1].rejectedAt && !pendency[1].acceptedAt).map(pendency => pendency[0])

  return pendencyFields.includes(fieldName)
}

const hasAllDocuments = (proposal: ProposalModel | null) => {
  if (!proposal) return false

  const { files, customerRef } = proposal
  const { personType } = customerRef

  const hasEnergyBillDocument = Boolean(files?.find(item => item.type === EProposalFileType.energyBill)?.status === EProposalFileStatus.accepted)
  if (personType === EPersonType.pf) {
    const hasFrontCustomerDocument = Boolean(files?.find(item => item.type === EProposalFileType.customerPfDocumentWithPhotoFront)?.status === EProposalFileStatus.accepted)
    const hasBackCustomerDocument = Boolean(files?.find(item => item.type === EProposalFileType.customerPfDocumentWithPhotoBack)?.status === EProposalFileStatus.accepted)

    return Boolean(hasFrontCustomerDocument && hasBackCustomerDocument && hasEnergyBillDocument)
  }

  const hasCustomerPjCardDocument = Boolean(files?.find(item => item.type === EProposalFileType.customerPjDocumentCard)?.status === EProposalFileStatus.accepted)
  const hasSocialContractDocument = Boolean(files?.find(item => item.type === EProposalFileType.customerPjSocialContract)?.status === EProposalFileStatus.accepted)
  
  return Boolean(hasCustomerPjCardDocument && hasSocialContractDocument && hasEnergyBillDocument)
}

const hasSolvedLegalRepresentativesPendencies = (proposal: ProposalModel | null) => {
  if (!proposal) return false

  const { customerRef } = proposal
  const { legalRepresentative, personType } = customerRef

  if (personType === EPersonType.pf) {
    return true
  }

  return Boolean(
    legalRepresentative?.every(item => {
      const { pendencies, files } = item
      const backDocument = files?.find(document => document.type === EProposalFileType.customerPjRepresentativeDocumentWithPhotoBack)
      const frontDocument = files?.find(document => document.type === EProposalFileType.customerPjRepresentativeDocumentWithPhotoFront)

      return Boolean(
        pendencies?.city.acceptedAt &&
        pendencies.name.acceptedAt &&
        pendencies.email.acceptedAt &&
        pendencies.phone.acceptedAt &&
        pendencies.state.acceptedAt &&
        pendencies.number.acceptedAt &&
        pendencies.zipcode.acceptedAt &&
        pendencies.document.acceptedAt &&
        pendencies.neighborhood.acceptedAt &&
        backDocument?.status === EProposalFileStatus.accepted &&
        frontDocument?.status === EProposalFileStatus.accepted
      )
    })
  )
}

export const getCanFormalizeProposal = (proposal: ProposalModel | null, isStepFormalization: boolean) => {
  if (!proposal) return false

  const { customerRef } = proposal
  const { pendencies } = customerRef

  const basePendencies = pendencies && Boolean(
    pendencies.uf.acceptedAt &&
    pendencies.name.acceptedAt &&
    pendencies.city.acceptedAt &&
    pendencies.email.acceptedAt &&
    pendencies.phone.acceptedAt &&
    pendencies.state.acceptedAt &&
    pendencies.street.acceptedAt &&
    pendencies.number.acceptedAt &&
    pendencies.zipcode.acceptedAt &&
    pendencies.document.acceptedAt &&
    pendencies.neighborhood.acceptedAt &&
    pendencies.dealershipName.acceptedAt &&
    pendencies.averageEnergyBillValue.acceptedAt
  )

  const hasSelectedConsortium = isStepFormalization ? Boolean(proposal.consortiumId !== null) : true 

  const allPendenciesSolved = basePendencies
  
  // Validate that seller and support are assigned for all proposals (when editing)
  const hasSellerAndSupport = Boolean(proposal.sellerRef && proposal.supportRef)
  
  return Boolean(
    allPendenciesSolved &&
    hasAllDocuments(proposal) &&
    hasSolvedLegalRepresentativesPendencies(proposal) &&
    hasSelectedConsortium &&
    hasSellerAndSupport &&
    proposal.fidelityMonths === 0 ? true : Boolean(proposal.fidelityMonths)
  )
}
